// import React, { useState, useEffect,useRef } from 'react';
// //import { useHistory } from 'react-router-dom';
// import Swal from 'sweetalert2';
// import { useForm } from 'react-hook-form'; // For form handling
// import Flatpickr from 'react-flatpickr'; // For date picker
// import 'flatpickr/dist/flatpickr.css'; // Flatpickr styles
// import '@fortawesome/fontawesome-free/css/all.min.css';
// import { Editor, EditorState, ContentState, convertToRaw, convertFromRaw, RichUtils } from 'draft-js';
// //import 'draft-js/dist/Draft.css';
import React, { useRef, useState, useEffect } from 'react';
import '@fortawesome/fontawesome-free/css/all.min.css';

const AddNewAnnouncements = ({ value, onChange, editorHeight, editorWidth }) => {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const [showAlignmentOptions, setShowAlignmentOptions] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  // State to track active formatting buttons
  const [activeButtons, setActiveButtons] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikeThrough: false,
    blockquote: false,
    orderedList: false
  });

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
    // Check initial content state
    if (editorRef.current) {
      checkContent();
      updateButtonStates();
    }
  }, [value]);

  // Function to check current formatting state
  const updateButtonStates = () => {
    const editor = editorRef.current;
    if (!editor) return;

    const selection = window.getSelection();
    if (selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;

    // Check if we're in a text node or element
    const element = container.nodeType === Node.TEXT_NODE ? container.parentElement : container;

    // Check formatting states
    const newActiveButtons = {
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      strikeThrough: document.queryCommandState('strikeThrough'),
      blockquote: element.closest('blockquote') !== null,
      orderedList: element.closest('ol') !== null
    };

    setActiveButtons(newActiveButtons);
  };

  // Add event listeners for selection changes
  useEffect(() => {
    const editor = editorRef.current;
    if (editor) {
      const handleSelectionChange = () => {
        updateButtonStates();
      };

      document.addEventListener('selectionchange', handleSelectionChange);
      editor.addEventListener('keyup', updateButtonStates);
      editor.addEventListener('mouseup', updateButtonStates);
      editor.addEventListener('input', updateButtonStates);

      return () => {
        document.removeEventListener('selectionchange', handleSelectionChange);
        editor.removeEventListener('keyup', updateButtonStates);
        editor.removeEventListener('mouseup', updateButtonStates);
        editor.removeEventListener('input', updateButtonStates);
      };
    }
  }, []);

  const handleInput = () => {
    onChange(editorRef.current.innerHTML);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      const currentNode = range.startContainer;

      // Check if we're inside a list
      const listItem = currentNode.nodeType === Node.TEXT_NODE
        ? currentNode.parentElement.closest('li')
        : currentNode.closest('li');

      if (listItem) {
        // We're in a list, let the browser handle it naturally
        // This will create a new list item and maintain proper cursor position
        return;
      }

      // Check if we're at the beginning of a line and user typed a number followed by period
      const textBeforeCursor = currentNode.textContent || '';
      const lineStart = textBeforeCursor.substring(0, range.startOffset);
      const numberPattern = /^\d+\.\s*$/;

      if (numberPattern.test(lineStart.trim())) {
        // User is trying to create a numbered list, let browser handle it
        return;
      }

      // For non-list content, prevent default and handle manually
      e.preventDefault();

      // Insert a line break
      const br = document.createElement('br');
      range.insertNode(br);

      // Move cursor after the line break
      range.setStartAfter(br);
      range.setEndAfter(br);
      selection.removeAllRanges();
      selection.addRange(range);

      // Trigger content check
      checkContent();
    }
  };

  const applyCommand = (command, value = null) => {
    const editor = editorRef.current;
    if (editor) {
      document.execCommand(command, false, value);
      editor.focus();
      // Update button states after applying command
      setTimeout(updateButtonStates, 0);
    }
  };

  const insertFormula = () => {
    const formula = prompt('Enter LaTeX formula');
    if (formula) {
      const editor = editorRef.current;
      editor.innerHTML += `<span class="math">${formula}</span>`;
      editor.focus();
      checkContent();
    }
  };

  const insertVideo = () => {
    const videoUrl = prompt('Enter video URL (YouTube, Vimeo, etc.) or embed code:');
    if (videoUrl) {
      const editor = editorRef.current;

      // Check if it's already an embed code (contains iframe)
      if (videoUrl.includes('<iframe')) {
        editor.innerHTML += videoUrl;
      } else {
        // Try to convert common video URLs to embed codes
        let embedCode = '';

        // YouTube URL patterns
        if (videoUrl.includes('youtube.com/watch') || videoUrl.includes('youtu.be/')) {
          let videoId = '';
          if (videoUrl.includes('youtube.com/watch')) {
            videoId = videoUrl.split('v=')[1]?.split('&')[0];
          } else if (videoUrl.includes('youtu.be/')) {
            videoId = videoUrl.split('youtu.be/')[1]?.split('?')[0];
          }
          if (videoId) {
            embedCode = `<iframe width="560" height="315" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
          }
        }
        // Vimeo URL patterns
        else if (videoUrl.includes('vimeo.com/')) {
          const videoId = videoUrl.split('vimeo.com/')[1]?.split('/')[0];
          if (videoId) {
            embedCode = `<iframe src="https://player.vimeo.com/video/${videoId}" width="560" height="315" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`;
          }
        }
        // If no recognized pattern, treat as direct embed code
        else {
          embedCode = videoUrl;
        }

        if (embedCode) {
          editor.innerHTML += `<div style="margin: 10px 0;">${embedCode}</div>`;
        } else {
          // Fallback: insert as a link
          editor.innerHTML += `<a href="${videoUrl}" target="_blank" rel="noopener noreferrer">${videoUrl}</a>`;
        }
      }

      editor.focus();
      checkContent();
    }
  };

  const handleImageUpload = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const editor = editorRef.current;
        editor.innerHTML += `<img src="${e.target.result}" style="max-width: 100%; height: auto;" />`;
        editor.focus();
        checkContent();
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAlignment = (command) => {
    applyCommand(command);
    setShowAlignmentOptions(false);
    checkContent();
  };

  const checkContent = () => {
    const editor = editorRef.current;
    const hasContent = editor.innerText.trim() ||
      editor.querySelector('img') ||
      editor.querySelector('iframe') ||
      editor.querySelector('video') ||
      editor.querySelector('audio') ||
      editor.querySelector('embed') ||
      editor.querySelector('object') ||
      editor.querySelector('span.math') ||
      editor.querySelector('div[style*="background-color: #1e1e1e"]');
    setIsEmpty(!hasContent);

    // Auto-resize the editor based on content
    if (editor) {
      editor.style.height = 'auto';
      const scrollHeight = editor.scrollHeight;
      const minHeight = editorHeight || 300; // Use prop or default to 200px
      const newHeight = Math.max(scrollHeight, minHeight);
      editor.style.height = newHeight + 'px';
    }

    handleInput();
    // Update button states when content changes
    updateButtonStates();
  };

  const handleBlackBarClick = () => {
    const editor = editorRef.current;
    const bar = `<div style="background-color: #1e1e1e; height: 30px; border-radius: 4px; margin: 10px 0; color: #fff; padding: 8px 12px; font-weight: bold;"> ${value} </div>`;
    editor.innerHTML += bar;
    editor.focus();
    checkContent();
  };

  useEffect(() => {
    const editor = editorRef.current;
    const observer = new MutationObserver(checkContent);
    if (editor) {
      observer.observe(editor, { childList: true, subtree: true, characterData: true });
    }
    return () => observer.disconnect();
  }, []);

  const toolbarStyle = {
    border: '1px solid #dee2e6',
    borderBottom: 'none',
    borderRadius: '4px 4px 0 0',
    padding: '8px',
    backgroundColor: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap'
  };

  const getButtonStyle = (isActive) => ({
    border: 'none',
    background: isActive ? 'var(--pmr-btn-primary)' : 'none',
    cursor: 'pointer',
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: isActive ? '#ffffff' : '#495057',
    borderRadius: '4px',
    transition: 'all 0.2s',
    padding: 0,
    fontSize: '14px',
    ':hover': {
      backgroundColor: isActive ? 'var(--pmr-btn-primary-hover)' : '#f8f9fa'
    }
  });

  const editorStyle = {
    border: '1px solid #dee2e6',
    borderTop: 'none',
    height: editorHeight ? `${editorHeight}px` : 'auto',
    width: editorWidth === '100%' ? '100%' : (editorWidth ? `${editorWidth}px` : '100%'),
    maxHeight: 'none',
    padding: '12px',
    borderRadius: '0 0 4px 4px',
    color: '#333',
    position: 'relative',
    fontSize: '14px',
    lineHeight: '1.5',
    backgroundColor: '#fff',
    boxSizing: 'border-box',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    overflowX: 'hidden',
    overflowY: 'visible',
    resize: 'vertical',
    minWidth: '0'
  };

  return (
    <div style={{ fontFamily: 'Arial, sans-serif' }}>
      <div style={toolbarStyle} className="editor-toolbar">
        <button onClick={() => applyCommand('bold')} style={getButtonStyle(activeButtons.bold)} title="Bold">
          <i className="fas fa-bold"></i>
        </button>
        <button onClick={() => applyCommand('italic')} style={getButtonStyle(activeButtons.italic)} title="Italic">
          <i className="fas fa-italic"></i>
        </button>
        <button onClick={() => applyCommand('underline')} style={getButtonStyle(activeButtons.underline)} title="Underline">
          <i className="fas fa-underline"></i>
        </button>
        <button onClick={() => applyCommand('strikeThrough')} style={getButtonStyle(activeButtons.strikeThrough)} title="Strikethrough">
          <i className="fas fa-strikethrough"></i>
        </button>
        <button onClick={() => applyCommand('formatBlock', '<blockquote>')} style={getButtonStyle(activeButtons.blockquote)} title="Blockquote">
          <i className="fas fa-quote-right"></i>
        </button>
        <button onClick={handleBlackBarClick} style={getButtonStyle(false)} title="Code Block">
          <i className="fas fa-code"></i>
        </button>
        <button onClick={() => applyCommand('insertOrderedList')} style={getButtonStyle(activeButtons.orderedList)} title="Numbered List">
          <i className="fas fa-list-ol"></i>
        </button>
        {/* <button onClick={() => applyCommand('createLink', prompt('Enter URL'))} style={getButtonStyle(false)} title="Insert Link">
          <i className="fas fa-link"></i>
        </button> */}
        <button onClick={handleImageUpload} style={getButtonStyle(false)} title="Insert Image">
          <i className="fas fa-image"></i>
        </button>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          accept="image/*"
        />
        <button onClick={insertVideo} style={getButtonStyle(false)} title="Insert Video">
          <i className="fas fa-video"></i>
        </button>
        <button onClick={insertFormula} style={{ ...getButtonStyle(false), fontFamily: 'serif' }} title="Insert Function Symbol">
          𝑓ₓ
        </button>
        <button onClick={() => applyCommand('insertText', prompt('Enter special text'))} style={{ ...getButtonStyle(false), fontFamily: 'serif' }} title="Insert Text Symbol">
          𝒯ₓ
        </button>
      </div>

      <div
        contentEditable
        ref={editorRef}
        suppressContentEditableWarning={true}
        onInput={checkContent}
        onKeyDown={handleKeyDown}
        className={`editor ${isEmpty ? 'empty' : ''}`}
        style={editorStyle}
        multiple={true}
      />

      <style>{`
        .editor {
          border: 1px solid #dee2e6 !important;
          border-top: none !important;
          overflow-y: visible !important;
          overflow-x: hidden !important;
          white-space: pre-wrap !important;
          word-wrap: break-word !important;
          overflow-wrap: break-word !important;
          word-break: break-word !important;
          box-sizing: border-box !important;
          resize: vertical !important;
          min-height: ${editorHeight || 200}px !important;
          scrollbar-width: none !important;
          -ms-overflow-style: none !important;
        }
        
        .editor::-webkit-scrollbar {
          display: none !important;
        }
        
        .editor.empty:not(:focus):before {
          content: 'Insert text here...';
          color: #adb5bd;
          position: absolute;
          pointer-events: none;
          padding: 12px;
          font-size: 14px;
          top: 0;
          left: 0;
        }
        
        .editor:focus {
          outline: none;
          border-color: var(--pmr-primary) !important;
          box-shadow: 0 0 0 0.15rem var(--pmr-primary-focus-ring);
        }
        
        .editor ol, .editor ul {
          margin: 8px 0;
          padding-left: 20px;
        }
        
        .editor li {
          margin: 4px 0;
          line-height: 1.5;
        }
        
        .editor ol li {
          list-style-type: decimal;
        }
        
        .editor ul li {
          list-style-type: disc;
        }
        
        .editor li:focus {
          outline: none;
        }
        
        .editor li:empty::before {
          content: '';
          display: inline-block;
          width: 1px;
        }

        /* Active button hover effects */
        .editor-toolbar button[style*="var(--pmr-btn-primary)"]:hover {
          background-color: var(--pmr-btn-primary-hover) !important;
        }

      `}</style>
    </div>
  );
};


export default AddNewAnnouncements;